import { supportedCommands } from '@spirit-testing/testoffice-ng-engine-core/catalog';
import { DATENFUNKTIONEN, signaturen } from '@spirit-testing/testoffice-ng-engine-core/datenfunktionen';
import { commandAt } from './tcdl-tokenizer';

export type TcdlCompletionSource = 'command' | 'library' | 'entry' | 'function';

/** What to suggest at the cursor and which part of the line a picked suggestion replaces. */
export interface TcdlCompletion {
  readonly source: TcdlCompletionSource;
  readonly prefix: string;
  /** Library before the dot; only for `entry`. */
  readonly library?: string;
  readonly replaceFrom: number;
  readonly replaceTo: number;
}

const UMLAUTS: Record<string, string> = { Ä: 'AE', Ö: 'OE', Ü: 'UE', ß: 'SS' };

/** Upper case, single spaces, umlauts spelled out: `drücke  t` and `DRUECKE T` compare equal. */
export function fold(name: string): string {
  return name
    .toUpperCase()
    .replace(/[ÄÖÜß]/gu, (c) => UMLAUTS[c])
    .replace(/\s+/gu, ' ')
    .trimStart();
}

// Only what the run executes, and only German names: the parser does not read every English one.
const ACTIONS = supportedCommands()
  .filter((e) => e.kind === 'action')
  .map((e) => e.de);
const VERIFICATIONS = supportedCommands()
  .filter((e) => e.kind === 'verification')
  .map((e) => e.de);
const COMMANDS = [...ACTIONS, ...VERIFICATIONS.map((v) => `IST ${v}`)];
const NEGATED = VERIFICATIONS.map((v) => `IST NICHT ${v}`);

/** Supported command names that start with what was typed, umlauts and case aside. */
export function commandSuggestions(prefix: string): string[] {
  const typed = fold(prefix);
  const candidates = typed.startsWith('IST N') ? [...COMMANDS, ...NEGATED] : COMMANDS;
  return candidates.filter((name) => fold(name).startsWith(typed));
}

// Every name, old names included, with its parameters: a test from Classic uses either.
const FUNCTIONS = DATENFUNKTIONEN.flatMap((f) => [f.name, ...f.aliase].flatMap((name) => signaturen(f, name)));

/** Data functions whose name starts with what was typed after `(fx)=`, case aside. */
export function functionSuggestions(prefix: string): string[] {
  const typed = prefix.toLowerCase();
  return FUNCTIONS.filter((signature) => signature.toLowerCase().startsWith(typed));
}

/** A picked function goes in as its name and `(`; the parameter names are only a hint. */
export function functionInsertText(signature: string): string {
  return signature.slice(0, signature.indexOf('(') + 1);
}

const AFTER_FX = /\(fx\)=([A-Za-z]*)$/u;

function wordEnd(line: string, from: number, stop: RegExp): number {
  return from + stop.exec(line.slice(from))![0].length;
}

/**
 * Where the cursor stands in a TCDL line: in the command, in the library name of the first
 * object after the comma, or after its dot in the entry name. `null` where nothing is offered,
 * e.g. in a string, in a variable, in a comment or behind a command that takes no object.
 */
export function completionContext(line: string, column: number): TcdlCompletion | null {
  const start = line.length - line.trimStart().length;
  if (column < start || line.slice(start).startsWith('//')) return null;

  const before = line.slice(start, column);
  // A data function can stand anywhere in a value, inside a string or a placeholder too.
  const fx = AFTER_FX.exec(before);
  if (fx) {
    return {
      source: 'function',
      prefix: fx[1],
      replaceFrom: column - fx[1].length,
      replaceTo: wordEnd(line, column, /^[A-Za-z]*/u),
    };
  }
  if (/["{]/u.test(before)) return null;
  const comma = before.indexOf(',');
  if (comma < 0) {
    // Inside a recognised command the whole command goes, or `DRU|ECKE TASTEN` would keep its TASTEN.
    const command = commandAt(line);
    const commandEnd = command?.entry ? command.start + command.length : 0;
    const replaceTo = Math.max(wordEnd(line, column, /^[^\s,"{]*/u), commandEnd);
    return { source: 'command', prefix: before, replaceFrom: start, replaceTo };
  }

  const segment = before.slice(comma + 1);
  if (/[,}]/u.test(segment)) return null;
  if (!commandAt(line)?.entry?.grammar.includes('Object')) return null;

  const wordStart = start + comma + 1 + (segment.length - segment.trimStart().length);
  const word = line.slice(wordStart, column);
  const dot = word.indexOf('.');
  if (dot < 0) {
    return {
      source: 'library',
      prefix: word,
      replaceFrom: wordStart,
      replaceTo: wordEnd(line, column, /^[^\s,."{]*/u),
    };
  }
  const library = word.slice(0, dot).trim();
  if (!library) return null;
  return {
    source: 'entry',
    prefix: word.slice(dot + 1),
    library,
    replaceFrom: wordStart + dot + 1,
    replaceTo: wordEnd(line, column, /^[^\s,"{]*/u),
  };
}
